import { ApprovalConfirmationComment } from "@/components/approvals/ApprovalConfirmationComment.tsx";
import { EmployeeListItem } from "@/components/employee/EmployeeListItem.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import {
  ApiApprovalRequestConfirmation,
  ApiApprovalRequestConfirmationStatus,
  ApiApprovalRequestDetails,
  ApiApprovalRequestStatus,
} from "@/types/approvals/approval.types.ts";
import {
  Stack,
  Text,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { twMerge } from "tailwind-merge";

export interface Props {
  request: ApiApprovalRequestDetails;
  hideCreator?: boolean;
  title?: string | undefined;
  showTextProgress?: boolean;
}

export const ApprovalsPersons = ({
  request,
  hideCreator = false,
  title,
  showTextProgress = false,
}: Props) => {
  const { t } = useTranslation();
  const { renderDateAndTime, renderTimeAgoShort } = useFormatter();
  const { openModal } = useModals();
  const [commentEditorKey, setCommentEditorKey] = useState<string | null>(null);
  const [savedCommentKeys, setSavedCommentKeys] = useState<Set<string>>(() => new Set());
  const confirmations = request.confirmations ?? [];
  const completedApprovalsCount = confirmations.filter(
    (confirmation) => confirmation.status === ApiApprovalRequestConfirmationStatus.approved
  ).length;
  const approvalsProgress =
    confirmations.length > 0
      ? t("approval:approvals_completed", {
          completed: completedApprovalsCount,
          total: confirmations.length,
        })
      : undefined;

  const getConfirmationCaption = (confirmation: ApiApprovalRequestConfirmation) => {
    let text: string;
    let needApprove = false;
    let approved = false;

    if (confirmation.status === ApiApprovalRequestConfirmationStatus.approved) {
      text = t("approval:request_status_approved");
      approved = true;
    } else if (confirmation.status === ApiApprovalRequestConfirmationStatus.rejected) {
      text = t("approval:request_status_rejected");
    } else if (confirmation.status === ApiApprovalRequestConfirmationStatus.skipped) {
      text = t("approval:request_status_skipped");
    } else if (confirmation.status === ApiApprovalRequestConfirmationStatus.queued) {
      text = t("approval:request_status_queued");
    } else {
      needApprove = true;
      text = t("approval:approve_required");
    }

    return (
      <Text
        className={twMerge(
          "text-secondary",
          needApprove && "text-warning",
          approved && "text-positive"
        )}
      >
        {text}
      </Text>
    );
  };

  const getConfirmationDate = (confirmation: ApiApprovalRequestConfirmation) => {
    if (confirmation.confirmed_at) {
      return confirmation.confirmed_at;
    } else if (confirmation.rejected_at) {
      return confirmation.rejected_at;
    }
    return null;
  };

  const getConfirmationTitle = (confirmation: ApiApprovalRequestConfirmation) => {
    if (confirmation.confirmed_at) {
      return renderDateAndTime(confirmation.confirmed_at);
    } else if (confirmation.rejected_at) {
      return renderDateAndTime(confirmation.rejected_at);
    }
    return undefined;
  };

  const addComment = (event: MouseEvent, confirmationKey: string) => {
    event.stopPropagation();
    setCommentEditorKey(confirmationKey);
  };

  const [syncedConfirmations, setSyncedConfirmations] = useState(request.confirmations);

  if (request.confirmations !== syncedConfirmations) {
    setSyncedConfirmations(request.confirmations);
    setSavedCommentKeys((currentKeys) => {
      const nextKeys = new Set(currentKeys);
      confirmations.forEach((confirmation, index) => {
        if (!confirmation.comment) return;

        nextKeys.delete(`confirmation-${confirmation.from_employee.id}-${index}`);
      });

      return nextKeys.size === currentKeys.size ? currentKeys : nextKeys;
    });
  }

  return (
    <>
      <Title
        caption={showTextProgress ? approvalsProgress : undefined}
        header={title ?? t("approval:approvers")}
        paddingTop
        paddingBottom
        size={"xs"}
      />
      <Stack>
        {request.created_by && !hideCreator && (
          <Stack className={"py-2 border-b border-dark/10"}>
            <EmployeeListItem
              behavior={"card"}
              employee={request.created_by}
              captionSlot={
                <Stack>
                  <Text>{getEmployeePositionTitle(request.created_by)}</Text>
                  <Text className={"text-secondary"}>{t("approval:request_created")}</Text>
                </Stack>
              }
              valueSlot={
                <Tooltip>
                  <TooltipTrigger>{renderTimeAgoShort(request.datetime_created_at)}</TooltipTrigger>
                  <TooltipContent>{renderDateAndTime(request.datetime_created_at)}</TooltipContent>
                </Tooltip>
              }
              onClick={
                request.created_by?.is_archived
                  ? undefined
                  : () => {
                      if (request.created_by) {
                        openModal(EmployeeViewModal, {
                          employeeId: request.created_by.id,
                        });
                      }
                    }
              }
              titleAttribute={renderDateAndTime(request.datetime_created_at)}
            />
          </Stack>
        )}

        {confirmations.map((confirmation, index) => {
          const date = getConfirmationDate(confirmation);
          const confirmationKey = `confirmation-${confirmation.from_employee.id}-${index}`;
          const canAddComment =
            !commentEditorKey &&
            !savedCommentKeys.has(confirmationKey) &&
            confirmation.acl.can_edit_comment &&
            request.status !== ApiApprovalRequestStatus.awaiting &&
            !confirmation.comment;

          return (
            <Stack
              key={confirmationKey}
              className={twMerge(
                "border-b py-3 border-dark/10",
                index === confirmations.length - 1 ? "border-b-0 pb-0" : ""
              )}
              gap={"smd"}
            >
              <EmployeeListItem
                behavior={"card"}
                employee={confirmation.from_employee}
                captionSlot={
                  <Stack gap={"xs"}>
                    <Stack>
                      <Text>{getEmployeePositionTitle(confirmation.from_employee)}</Text>
                      {getConfirmationCaption(confirmation)}
                    </Stack>
                  </Stack>
                }
                valueSlot={
                  <Stack items={"end"}>
                    {date && (
                      <Tooltip>
                        <TooltipTrigger>{renderTimeAgoShort(new Date(date))}</TooltipTrigger>
                        <TooltipContent>{renderDateAndTime(new Date(date))}</TooltipContent>
                      </Tooltip>
                    )}
                    {canAddComment ? (
                      <Text
                        className={"text-secondary underline hover:no-underline cursor-pointer"}
                        onClick={(event) => addComment(event, confirmationKey)}
                      >
                        {t("common:add_comment")}
                      </Text>
                    ) : undefined}
                  </Stack>
                }
                titleAttribute={getConfirmationTitle(confirmation)}
                onClick={
                  confirmation.from_employee.is_archived
                    ? undefined
                    : () => {
                        openModal(EmployeeViewModal, {
                          employeeId: confirmation.from_employee.id,
                        });
                      }
                }
              />

              <ApprovalConfirmationComment
                confirmation={confirmation}
                request={request}
                isAddingComment={commentEditorKey === confirmationKey}
                onEditEnd={() => setCommentEditorKey(null)}
                onCommentSaved={(comment) => {
                  if (!comment) return;

                  setSavedCommentKeys((currentKeys) => {
                    const nextKeys = new Set(currentKeys);
                    nextKeys.add(confirmationKey);
                    return nextKeys;
                  });
                }}
              />
            </Stack>
          );
        })}
      </Stack>
    </>
  );
};
